import { useAppSelector } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { changeSubmitted, formStore, notifyEntityCreated, removeForm, seed, useFormRepository } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FormProps, defaultObjectFormRowClassName } from './form.component';

interface ViewProps<S extends object> extends ReturnType<typeof useFormRepository> {
    header: React.ReactNode;
    rowClass: string;
    show: (keyof S | Array<keyof S>)[];
    submitted: boolean;
    submit: (e: any) => void;
    additionalSubmit: (e: any) => void;
}

const useForm = <T extends object, S extends object>(props: FormProps<T, S>): ViewProps<S> => {
    const repository = useFormRepository({ dispatch: props.dispatch, initialValue: props.value, id: props.useStaticId ? props.id : undefined });
    const store = useAppSelector(useSelector, formStore);
    const form = store[repository.formId];

    useEffect(() => {
        return () => {
            props.dispatch(removeForm({ formId: repository.formId }));
        };
    }, []);

    useEffect(() => {
        if (form && props.onChange && form.value) props.onChange(form.value as S);
    }, [form]);

    useEffect(() => {
        props.formIdCallback && props.formIdCallback(repository.formId);
    }, [repository.formId]);

    const header = useMemo(() => {
        if (props.label) return props.label;
        const h = props.headerOverwrite ? props.headerOverwrite : props.type.replace(/_/g, ' ');
        if (props.object) return CommonMessage.FORMS.UPDATE_OBJECT(h);
        return CommonMessage.FORMS.CREATE_OBJECT(h);
    }, [props.label]);
    const show = useMemo(() => getUniqueSet(props.show ?? [], props.required ?? []), [props.show, props.required]);

    const submit = (e: any) => {
        if (props.onSubmit === undefined || !store[repository.formId]) return;
        e.preventDefault();
        props.dispatch(changeSubmitted({ formId: repository.formId, value: true }));
        props
            .onSubmit(store[repository.formId].value as S)
            .then((result) => {
                const ent = (props.type[0].toUpperCase() + props.type.slice(1)).split('_').join(' ');
                const message = props.object ? CommonMessage.FORMS.SUCCESSFULLY_UPDATED_ENTITY(ent) : CommonMessage.FORMS.SUCCESSFULLY_CREATED_ENTITY(ent);
                ToastUtil.success(props.successMessageOverwrite ? props.successMessageOverwrite : message);
                if (typeof result !== 'undefined') {
                    props.dispatch(seed({ key: props.type, value: [result as any] }));
                    if (!props.object) notifyEntityCreated(props.dispatch, props.type);
                    props.onSubmitSuccess && props.onSubmitSuccess(result);
                } else {
                    props.onVoidSuccess && props.onVoidSuccess(result);
                    if (!props.object) notifyEntityCreated(props.dispatch, props.type);
                }
                props.dispatch(removeForm({ formId: repository.formId }));
            })
            .catch((e) => {
                if (props.errorMessageOverwrite) {
                    props.errorMessageOverwrite(e);
                } else {
                    const entityTypeKey = props.type.toUpperCase() as keyof Omit<typeof CommonMessage.OBJECTS, 'DEFAULT'>;
                    if (entityTypeKey in CommonMessage.OBJECTS) {
                        const entityObject = CommonMessage.OBJECTS[entityTypeKey as keyof typeof CommonMessage.OBJECTS];
                        const entityType = 'DEFAULT' in entityObject ? entityObject['DEFAULT'] : 'Unknown Entity';
                        ErrorHandler.handleError({ error: e, entity_type: entityType });
                    } else {
                        Logger.warn(`Entity type ${props.type} not found in CommonMessage.OBJECTS`);
                        ErrorHandler.handleError({ error: e, entity_type: props.type });
                    }
                }
            })
            .finally(() => props.dispatch(changeSubmitted({ formId: repository.formId, value: false })));
    };

    const additionalSubmit = (e: any) => {
        if (props.additionalSubmit === undefined || !store[repository.formId]) return;
        e.preventDefault();
        props.dispatch(changeSubmitted({ formId: repository.formId, value: true }));
        props.additionalSubmit
            .onClick(store[repository.formId].value as S)
            .then((result) => {
                const ent = (props.type[0].toUpperCase() + props.type.slice(1)).split('_').join(' ');
                const message = props.object ? CommonMessage.FORMS.SUCCESSFULLY_UPDATED_ENTITY(ent) : CommonMessage.FORMS.SUCCESSFULLY_CREATED_ENTITY(ent);
                ToastUtil.success(props.successMessageOverwrite ? props.successMessageOverwrite : message);
                if (typeof result !== 'undefined') {
                    props.dispatch(seed({ key: props.type, value: [result as any] }));
                    if (!props.object) notifyEntityCreated(props.dispatch, props.type);
                    props.additionalSubmit?.onSubmitSuccess && props.additionalSubmit.onSubmitSuccess(result);
                } else {
                    props.additionalSubmit?.onVoidSuccess && props.additionalSubmit.onVoidSuccess(result);
                    if (!props.object) notifyEntityCreated(props.dispatch, props.type);
                }
                props.dispatch(removeForm({ formId: repository.formId }));
            })
            .catch((e) => {
                const entityTypeKey = props.type.toUpperCase() as keyof Omit<typeof CommonMessage.OBJECTS, 'DEFAULT'>;
                if (entityTypeKey in CommonMessage.OBJECTS) {
                    const entityObject = CommonMessage.OBJECTS[entityTypeKey as keyof typeof CommonMessage.OBJECTS];
                    const entityType = 'DEFAULT' in entityObject ? entityObject['DEFAULT'] : 'Unknown Entity';
                    ErrorHandler.handleError({ error: e, entity_type: entityType });
                } else {
                    Logger.warn(`Entity type ${props.type} not found in CommonMessage.OBJECTS`);
                    ErrorHandler.handleError({ error: e, entity_type: props.type });
                }
            })
            .finally(() => props.dispatch(changeSubmitted({ formId: repository.formId, value: false })));
    };

    return {
        ...repository,
        header,
        rowClass: props.rowClassName ?? defaultObjectFormRowClassName,
        show,
        submitted: form?.submitted ?? false,
        submit,
        additionalSubmit
    };
};

export default useForm;

type Element = any | any[];

function getUniqueSet(arrA: Element[], arrB: Element[]): Element[] {
    const combinedArray: Element[] = [...arrA, ...arrB];
    const seen = new Set<string>();
    const uniqueResult: Element[] = [];

    for (const item of combinedArray) {
        const key = Array.isArray(item) ? JSON.stringify(item) : item;

        if (!seen.has(key)) {
            seen.add(key);
            uniqueResult.push(item);
        }
    }

    return uniqueResult;
}
